/* eslint-disable array-element-newline */

// Implements an equivalents of crypt(3).
// Currently, only the SHA-512 variant.
// Algorithm description: https://www.akkadia.org/drepper/SHA-crypt.txt
// C implementation for reference:
//   See the `crypt/sha512-crypt.c` in commit 3edca7f545 in glibc
//   Sources available at: https://sourceware.org/glibc/sources.html

import { sha512 } from '@noble/hashes/sha2'
import { randomBytes, toBytes } from '@noble/hashes/utils'

const b64Chars = './0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'

const sha512ByteOrder = [
  42, 21, 0,
  1, 43, 22,
  23, 2, 44,
  45, 24, 3,
  4, 46, 25,
  26, 5, 47,
  48, 27, 6,
  7, 49, 28,
  29, 8, 50,
  51, 30, 9,
  10, 52, 31,
  32, 11, 53,
  54, 33, 12,
  13, 55, 34,
  35, 14, 56,
  57, 36, 15,
  16, 58, 37,
  38, 17, 59,
  60, 39, 18,
  19, 61, 40,
  41, 20, 62,
  63,
]

const clearArray = arr => {
  if (!arr) {
    return
  }

  for (let i = 0; i < arr.length; i++) {
    arr[i] = 0
  }
}

/* Please note, this base64 encoding differs from what is most commonly used.
   It doesn't use '+'. Also, it has no padding.
   For details, see https://en.wikipedia.org/wiki/Base64
 */
const b64Encode = (inputArr, orderMap = null, outputChars = []) => {
  for (let tripletStart = 0; tripletStart < inputArr.length; tripletStart += 3) {
    let value = 0
    let howMany = 4
    for (let byteNumber = 2; byteNumber >= 0; byteNumber--) {
      let inputIndex = tripletStart + byteNumber
      if (orderMap) {
        if (inputIndex < orderMap.length) {
          inputIndex = orderMap[inputIndex]
        } else {
          inputIndex = inputArr.length // Out of bounds - forces '0' and decreases howMany
        }
      }
      if (inputIndex >= inputArr.length) {
        howMany -= 1
      } else {
        value = (value << 8) | inputArr[inputIndex]
      }
    }

    for (let i = 0; i < howMany; i++) {
      outputChars.push(b64Chars[value & 0x3F])
      value >>>= 6
    }

    value = 0
    howMany = 0
  }

  const result = outputChars.join('')
  clearArray(outputChars)
  return result
}

export const cryptSha512 = (passwordText, salt_text_in = null) => {
  // Note: snake_case is intentional here, so that all those As, Bs and Cs have a space to "breath"
  // Note: sha512.digest() calls sha512.destroy() internally, clearing its internal state
  //       See HashMD in node_modules/@noble/hashes/esm/_md.js

  if (passwordText === null || passwordText === undefined) {
    return undefined
  }

  let password, salt, salt_text
  let hash_A, digest_A, digest_B
  let hash_DP, digest_DP, sequence_P, sequence_P_UIA
  let hash_DS, digest_DS, sequence_S, sequence_S_UIA
  let digest_AC, hash_C, digest_C
  let counter, first_N, output_chars

  try {
    // Note: Don't use bytesToUtf8 from '@noble/hashes/utils' - it may add a prefix of repeated '77+9',
    // eliminating most or all randomness in the salt!
    // One terrible example I've witnessed was '77+977+977+977+9' !!! ¯\_(ツ)_/¯
    // Also, don't use Base64.toBase64, as this is not your standard Base64 encoding.
    salt_text = (salt_text_in ? salt_text_in : b64Encode(randomBytes(16))).substring(0, 16)

    salt = toBytes(salt_text)
    password = toBytes(passwordText)

    // Steps 1-3
    hash_A = sha512.create().update(password).update(salt)

    // Steps 4-8
    digest_B = sha512.create().update(password).update(salt).update(password).digest()

    // Step 9
    counter = Math.floor(password.length / 64)
    for (; counter > 0; counter--) {
      hash_A.update(digest_B)
    }

    // Step 10
    first_N = digest_B.slice(0, password.length % 64)
    hash_A.update(first_N)

    // Step 11
    for (counter = password.length; counter !== 0; counter >>>= 1) {
      if (counter & 1) {
        hash_A.update(digest_B)
      } else {
        hash_A.update(password)
      }
    }

    // Step 12
    digest_A = hash_A.digest()

    // Steps 13-15
    hash_DP = sha512.create()
    for (counter = password.length; counter > 0; counter--) {
      hash_DP.update(password)
    }
    digest_DP = hash_DP.digest()

    // Step 16
    sequence_P = []
    for (counter = 0; counter < password.length; counter++) {
      sequence_P.push(digest_DP[counter % digest_DP.length])
    }
    sequence_P_UIA = Uint8Array.from(sequence_P)

    // Steps 17-19
    hash_DS = sha512.create()
    for (counter = 16 + digest_A[0]; counter > 0; counter--) {
      hash_DS.update(salt)
    }
    digest_DS = hash_DS.digest()

    // Step 20
    sequence_S = []
    for (counter = 0; counter < salt.length; counter++) {
      sequence_S.push(digest_DS[counter % digest_DS.length])
    }
    sequence_S_UIA = Uint8Array.from(sequence_S)

    // Step 21
    digest_AC = digest_A
    for (counter = 0; counter < 5000; counter++) {
      hash_C = sha512.create()
      if ((counter % 2) !== 0) { // 21.b
        hash_C.update(sequence_P_UIA)
      } else { // 21.c
        hash_C.update(digest_AC)
      }

      if ((counter % 3) !== 0) { // 21.d
        hash_C.update(sequence_S_UIA)
      }

      if ((counter % 7) !== 0) { // 21.e
        hash_C.update(sequence_P_UIA)
      }

      if ((counter % 2 !== 0)) { // 21.f
        hash_C.update(digest_AC)
      } else { // 21.g
        hash_C.update(sequence_P_UIA)
      }

      let round_digest = hash_C.digest()
      hash_C = null
      clearArray(digest_AC)
      digest_AC = round_digest
      round_digest = null
    }

    // Step 22.
    output_chars = ['$6$', salt_text, '$']

    return b64Encode(digest_AC, sha512ByteOrder, output_chars)
  } catch (e) {
    // Shouldn't happen... but if it does, don't propagate the exception!
    return undefined
  } finally {
    clearArray(password)
    clearArray(salt)
    clearArray(digest_A)
    clearArray(digest_B)
    clearArray(digest_DP)
    clearArray(sequence_P)
    clearArray(sequence_P_UIA)
    clearArray(digest_DS)
    clearArray(sequence_S)
    clearArray(sequence_S_UIA)
    clearArray(digest_AC)
    clearArray(digest_C)
    clearArray(first_N)
    clearArray(output_chars)
    password = null
    salt = null
    salt_text = null
    hash_A = null
    digest_A = null
    digest_B = null
    hash_DP = null
    digest_DP = null
    sequence_P = null
    sequence_P_UIA = null
    hash_DS = null
    digest_DS = null
    sequence_S = null
    sequence_S_UIA = null
    digest_AC = null
    hash_C = null
    digest_C = null
    counter = null
    first_N = null
    output_chars = null
  }
}

/*
    For salt set to '0B2F3NoF' (encoded text 'BCDEFG') and passwordText = 'ala123', it should yield:
    $6$0B2F3NoF$2vRxth7OiAWXop7zR/Uv6hhkLZDE4dV2Aju4kY3.4J6YNR7QkJu3N/O2gG9HJn5qwX3BCpUkhU.Z3da4nepn/1

    The expected contents of variables for that pair of salt and password are as follows:

            digest_B
              120  77 233  24 229 235 173   0  81  65  74 245   3  34  66 220
               47 101  39  92  79  95 156  28 149 156  64  53  39  83 198 203
              164  83 119 181 107  86  49 239 103 204  11 169 179  60 253 189
              141 252 181  93 136 110 200 105 177  67  21 192 249 174 146 127

            digest_A
               39 121 190 184 244  80  37 155 250  51  89 174 159  11 209  97
              114  79  34 109 204 178 227 219  35 164  79  23 122 157 154 139
              197  22 117  27 192  32  39  34  61 168 247  59 235 121   4  23
              171  84 220  83 105   6  23 229  82 114 145   6 162  27  14  91

            digest_DP
               75 233 119 112  25 126  64 161 212  34  32 138  13  58 166 138
              150 116  83 160   1  98  38 254 149 112 214 131 148 196 183 103
              123  65   1 188 146  99  64 178 223 161  62  93  83  12 104 120
               87 232   8  27 251  34  15 178 142  41 106  13 227 240  83 158

            sequence_P_UIA
               75 233 119 112  25 126

            digest_DS
              200 162 202  60 176  71  62  15  38  57 188 188  33  91  88  45
              153 231  31 112  44  64  53  48 149 171  97  82  72 127  61 106
              170 134  88  27  36 108  86 197  84 225 132  99 172 183  38 110
               22 175 230  38  89 102  33  72 231  56  52 182  73   1 177   9

            sequence_S_UIA
              200 162 202  60 176  71  62  15

            First 10 first iterations of the final calculation loop:
              digest_AC[0]
                 97 184 247 190 197 125 162  18   9  63 155 101  48  80   5  10
                236 132 238  96 130 175 163  84  51 190  61 153 118 105 129 189
                 98  43  55 164 118 246  48 228 138   1   5  57 241  46 250 184
                179 252  40 180  77 183 120 153 126 125 120 161 203  42  85  79

              digest_AC[1]
                110 249 192 204 159  33 183  35 134  27 155 118  94 195 193 174
                108 243 174  20  78 208 126 141 153 106 192 121 215  25 137 168
                194  77 199 218   4 214 237  89 143 114 230  25  89  78  80 243
                229  39 240 164  57 144 123 203  32 123  93 239 114  60 114 129

              digest_AC[2]
                  5 169 231  44  98  55  69 110 153 149  53 225 212  13 232 203
                 27 229 211 220  32 228 128   5 206 191  34 147 202 162 208 178
                 39  71  20 153   7 148 122 222  46  12  24 170  28 211 187  64
                231 116 121  59  55  19 223 186 183  78  27  65 103  60 128 215

              digest_AC[3]
                123 128  45 188 251 199  49  34  53  99 223 115 248  51 148 170
                138 226 194 204 165  42 129  43  14 169 203  84  39 176  92  21
                  2  70 237 105  93 101  63 213  18 213 102  54 193 198 246   0
                110  41 119  75  63 205 153 166 129 138 136 223  53 112  96  17

              digest_AC[4]
                 52 129   2 104 253  76 103  18 120 182  67 170  56 108  69 158
                117  63 240   0 195 154  36  34  27   8 153  71  95  74  34 158
                147  52 150  77 123  71   1  41  43   5 127  57  78 208  83 118
                196 116  13  46 176 100 112  22  29 151  50 251 141 170 224  48

              digest_AC[5]
                255 142  85 128 189 225 167 229 193 114  91   6 151  72  64  91
                117  51 135 159 170 108  70  66 193 247  56 245 208 196  70 171
                129  43  31  70 145  86  52 227 111  69 155 189  71 199 222  91
                101 215 123 253   0  41 184 180  47  66 248  44 134 208 198 187

              digest_AC[6]
                158 229 117 141  29  36 220  24  10 167 115 218 193  61 155 149
                  7  36  60  63 232  85  40 194 227 202 228  22 166  93 135   2
                 79  66  91 100  25 189  98  73  27  27 127  55 182 173  47 181
                 46 168 176  98 173 214 130 121 173 137  88  29 241  32  31  23

              digest_AC[7]
                101 168  67 118  49  10  90 181 194 171 218 176 224 120  96 169
                130 117 248 143  59 106  86 139 150 209 212 132 124 199 111 119
                125 188  89 142   6  78   0  13 164  71 198  99 251  24  53 136
                235   2 129   7 204  72  65 223 123 241  26 122  25 160  83 142

              digest_AC[8]
                199  63 247 103  88  82 141 228  60  30 149  54 221 183 228 121
                 93 117 173  19 164 209 176 185  24  93 192 194  31  14 144 218
                190 240 237 105 204 244 173 253  99 180 158 139 245  10  32 154
                115 132 159 121 157  28  85 135  37 166 244 187 147 228  12  91

              digest_AC[9]
                239  10 105 142 184  89  75  45 215  28  86  67 136 119 177  25
                 96 159 158 250  10 190  18 122 114   8  58 194 195 220  30 198
                 37  53  75 117  72  83  43 130 163 209  91  18  87  51 243 160
                230 230 191  64  36  77   0  82 231 170  64 208   3 227 126  37

            After that loop is finished:
              digest_AC
                245 121  35 252  93 219  64  70 171   0  70 151  23  89 180 216
                252  13 148  69  90 222 104  46 157 238  72 249  18 204  89 144
                 89 165  17 172 124  52  78   8  26 179 196 155 142 116   0 194
                 87  26  27  48 133 112 112 160  76 213  88 194  45 106 207 193

 */
